<template>
  <div
    class="expression-editor"
    :class="{ invalid: isInvalid, fullscreen: isFullscreen, disableMinHeight: disableMinHeight }"
    :data-id="name"
  >
    <textarea class="expression-editor-textarea" @change="handleTextAreaChange" ref="textarea"></textarea>
    <div class="change-style" v-if="lineCount !== 1 && showTools">
      <el-tooltip effect="dark" :content="$i18n.t('fix_tabulators')" placement="top-start">
        <span class="material-icons" @click="formatCode"> sort </span>
      </el-tooltip>
      <el-tooltip effect="dark" :content="$i18n.t('toggle_fullscreen')" placement="top-start">
        <span class="material-icons" @click="toggleFullscreen">
          {{ fullScreenIcon }}
        </span>
      </el-tooltip>
      <el-tooltip effect="dark" :content="$i18n.t('change_code_style')" placement="top-start">
        <span class="material-icons" @click="showOptions"> style </span>
      </el-tooltip>
      <el-select ref="selectRef" v-model="currentTheme">
        <el-option v-for="theme in codeMirrorThemes" :key="theme" :label="theme" :value="theme"> </el-option>
      </el-select>
    </div>
    <div class="code-syntax-check" v-if="!disableSyntaxCheck && syntaxErrors">
      {{ syntaxErrors }}
    </div>
  </div>
</template>

<script>
import { isNil, get } from 'lodash'
import CodeMirror from 'codemirror'
import tern from 'tern'
// language
import 'codemirror/mode/vue/vue.js'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/htmlmixed/htmlmixed'
import 'codemirror/mode/css/css'
import 'codemirror/mode/sql/sql'

// theme css
import 'codemirror/theme/3024-day.css'
import 'codemirror/theme/3024-night.css'
import 'codemirror/theme/abbott.css'
import 'codemirror/theme/abcdef.css'
import 'codemirror/theme/ambiance.css'
import 'codemirror/theme/ayu-dark.css'
import 'codemirror/theme/ayu-mirage.css'
import 'codemirror/theme/base16-dark.css'
import 'codemirror/theme/base16-light.css'
import 'codemirror/theme/bespin.css'
import 'codemirror/theme/blackboard.css'
import 'codemirror/theme/cobalt.css'
import 'codemirror/theme/colorforth.css'
import 'codemirror/theme/darcula.css'
import 'codemirror/theme/dracula.css'
import 'codemirror/theme/duotone-dark.css'
import 'codemirror/theme/duotone-light.css'
import 'codemirror/theme/eclipse.css'
import 'codemirror/theme/elegant.css'
import 'codemirror/theme/erlang-dark.css'
import 'codemirror/theme/gruvbox-dark.css'
import 'codemirror/theme/hopscotch.css'
import 'codemirror/theme/icecoder.css'
import 'codemirror/theme/idea.css'
import 'codemirror/theme/isotope.css'
import 'codemirror/theme/juejin.css'
import 'codemirror/theme/lesser-dark.css'
import 'codemirror/theme/liquibyte.css'
import 'codemirror/theme/lucario.css'
import 'codemirror/theme/material.css'
import 'codemirror/theme/material-darker.css'
import 'codemirror/theme/material-palenight.css'
import 'codemirror/theme/material-ocean.css'
import 'codemirror/theme/mbo.css'
import 'codemirror/theme/mdn-like.css'
import 'codemirror/theme/midnight.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/theme/moxer.css'
import 'codemirror/theme/neat.css'
import 'codemirror/theme/neo.css'
import 'codemirror/theme/night.css'
import 'codemirror/theme/nord.css'
import 'codemirror/theme/oceanic-next.css'
import 'codemirror/theme/panda-syntax.css'
import 'codemirror/theme/paraiso-dark.css'
import 'codemirror/theme/paraiso-light.css'
import 'codemirror/theme/pastel-on-dark.css'
import 'codemirror/theme/railscasts.css'
import 'codemirror/theme/rubyblue.css'
import 'codemirror/theme/seti.css'
import 'codemirror/theme/shadowfox.css'
import 'codemirror/theme/the-matrix.css'
import 'codemirror/theme/tomorrow-night-bright.css'
import 'codemirror/theme/tomorrow-night-eighties.css'
import 'codemirror/theme/ttcn.css'
import 'codemirror/theme/twilight.css'
import 'codemirror/theme/vibrant-ink.css'
import 'codemirror/theme/xq-dark.css'
import 'codemirror/theme/xq-light.css'
import 'codemirror/theme/yeti.css'
import 'codemirror/theme/yonce.css'
import 'codemirror/theme/zenburn.css'
import 'codemirror/theme/material.css'

import 'codemirror/lib/codemirror.css'
// require active-line.js
import 'codemirror/addon/selection/active-line.js'
// styleSelectedText
import 'codemirror/addon/selection/mark-selection.js'
// hint
import 'codemirror/addon/edit/trailingspace.js'
import 'codemirror/addon/hint/show-hint.js'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/javascript-hint.js'
import 'codemirror/addon/hint/html-hint.js'
import 'codemirror/addon/hint/css-hint.js'
import 'codemirror/addon/hint/sql-hint.js'
import 'codemirror/addon/edit/closetag.js'
import 'codemirror/addon/edit/closebrackets.js'

// highlightSelectionMatches
import 'codemirror/addon/scroll/annotatescrollbar.js'
import 'codemirror/addon/search/matchesonscrollbar.js'
import 'codemirror/addon/search/match-highlighter.js'
// keyMap
import 'codemirror/mode/clike/clike.js'
import 'codemirror/addon/edit/matchbrackets.js'
import 'codemirror/addon/edit/matchtags.js'
import 'codemirror/addon/edit/trailingspace.js'
import 'codemirror/addon/comment/comment.js'
import 'codemirror/addon/dialog/dialog.js'
import 'codemirror/addon/dialog/dialog.css'
import 'codemirror/addon/search/searchcursor.js'
import 'codemirror/addon/search/search.js'
import 'codemirror/keymap/sublime.js'
// foldGutter
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/brace-fold.js'
import 'codemirror/addon/fold/comment-fold.js'
import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.js'
import 'codemirror/addon/fold/indent-fold.js'
import 'codemirror/addon/fold/markdown-fold.js'
import 'codemirror/addon/fold/xml-fold.js'
// themes
import 'codemirror/theme/elegant.css'
import 'codemirror/theme/erlang-dark.css'
import 'codemirror/theme/lesser-dark.css'
import 'codemirror/theme/eclipse.css'
import 'codemirror/theme/neat.css'
import 'codemirror/theme/darcula.css'
import 'codemirror/theme/xq-light.css'
// tern
import 'codemirror/addon/tern/tern.js'
import 'codemirror/addon/tern/tern.css'
// autoRefresh
import 'codemirror/addon/display/autorefresh.js'

import ecmascript from './ecmascript.json'
import { codeMirrorThemes } from '../../utils'
import { getCompletions } from './completions'
import { AI_ASSISTANTS, EDITOR_MODES } from '../../consts'

const ignoredKeyCodesForAutocomplete = [
  'Backspace',
  'Tab',
  'Enter',
  'Escape',
  'ArrowRight',
  'ArrowLeft',
  'ArrowUp',
  'ArrowDown',
  'AltLeft',
  'AltRight',
  'ShiftLeft',
  'ShiftRight',
  'MetaLeft',
  'MetaRight',
]
const allEvents = [
  'scroll',
  'changes',
  'beforeChange',
  'cursorActivity',
  'keyHandled',
  'inputRead',
  'electricInput',
  'beforeSelectionChange',
  'viewportChange',
  'swapDoc',
  'gutterClick',
  'gutterContextMenu',
  'focus',
  'blur',
  'refresh',
  'optionChange',
  'scrollCursorIntoView',
  'update',
]

CodeMirror.registerHelper('hint', 'core', function (editor) {
  const cur = editor.getCursor()
  const curLine = editor.getLine(cur.line)
  let start = cur.ch
  let end = start
  while (end < curLine.length && /[\w$]+/.test(curLine.charAt(end))) ++end
  while (start && /[\w$]+/.test(curLine.charAt(start - 1))) --start
  const curWord = start !== end && curLine.slice(start, end)
  const regex = new RegExp('^' + curWord, 'i')
  return {
    list: (!curWord
      ? []
      : window.CORE.hintDictionary.filter(function (item) {
          return item.match(regex)
        })
    ).sort(),
    from: CodeMirror.Pos(cur.line, start),
    to: CodeMirror.Pos(cur.line, end),
  }
})

/**
 * @class ExpressionEditor
 *
 * @description This component renders a code editor that is used to write expressions for form fields
 */
export default {
  name: 'expression-editor',
  props: {
    assistant: {
      type: String,
      default: AI_ASSISTANTS.DEFAULT_CODE_WRITER,
    },
    showTools: {
      type: Boolean,
      default: false,
    },
    disableSyntaxCheck: {
      type: Boolean,
      default: false,
    },
    code: String,
    value: {
      type: [String, Object],
    },
    marker: Function,
    unseenLines: Array,
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Object,
      default: () => ({
        mode: 'text/javascript',
      }),
    },
    disableMinHeight: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
    },
    globalOptions: {
      type: Object,
      default: () => ({}),
    },
    globalEvents: {
      type: Array,
      default: () => [],
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'expressionEditor',
    },
    completionSources: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      selectRef: null,
      codeMirrorThemes: codeMirrorThemes,
      currentTheme: localStorage.getItem('codeMirror-theme') || 'darcula',
      content: '',
      codemirror: null,
      cminstance: null,
      ecmascriptDefs: null,
      ternConfig: {},
      textAreaValue: '',
      lineCount: 1,
      isFullscreen: false,
    }
  },
  watch: {
    options: {
      deep: true,
      handler() {
        const options = this.getCmOptions()
        for (const key in options) {
          this.cminstance.setOption(key, options[key])
        }
      },
    },
    code(newVal) {
      this.codeChangeHandler(newVal)
    },
    value(newVal) {
      this.codeChangeHandler(newVal)
    },
    currentTheme(newVal) {
      this.cminstance.setOption('theme', newVal)
      localStorage.setItem('codeMirror-theme', newVal)
    },
  },
  computed: {
    fullScreenIcon() {
      return this.isFullscreen ? 'fullscreen_exit' : 'fullscreen'
    },
    replacementMap() {
      const mode = this.cminstance.getOption('mode')
      return getCompletions(mode, this.assistant, this.completionSources) || []
    },
    syntaxErrors() {
      if (this.options.mode !== 'text/javascript' || this.lineCount <= 1) return false
      if (this.cminstance) {
        const value = this.cminstance.getValue()
        return this.isValidJavaScript(value)
      } else {
        return false
      }
    },
  },
  methods: {
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },
    formatCode() {
      const CodeMirror = this.cminstance
      var totalLines = CodeMirror.lineCount()
      CodeMirror.operation(function () {
        for (var i = 0; i < totalLines; i++) {
          CodeMirror.indentLine(i, 'smart')
        }
      })
    },
    isValidJavaScript(code) {
      try {
        new Function(code)
        return null
      } catch (e) {
        return e.toString()
      }
    },
    showOptions() {
      const ref = this.$refs.selectRef.$el
      if (ref) ref.click()
    },
    handleTextAreaChange(evt) {
      const value = evt.target.value
      this.cminstance.setValue(value)
    },
    getCmOptions: function () {
      const defaultOptions = {
        theme: this.currentTheme,
        height: 'auto',
        viewportMargin: Infinity,
        showTrailingSpace: true,
        lineNumbers: true,
        readOnly: this.readOnly || false,
        autoRefresh: true,
        lineWrapping: true,
      }

      const options = Object.assign({}, defaultOptions, this.globalOptions, this.options)

      if (!options.theme) {
        options.theme = defaultOptions.theme
      }

      return options
    },
    initialize() {
      this.codemirror = CodeMirror.fromTextArea(this.$refs.textarea, this.getCmOptions())
      this.cminstance = this.codemirror
      this.cminstance.setValue(this.content)
      this.cminstance.setOption('theme', this.currentTheme)
      this.cminstance.setSize('100%', 'fit-content')
      this.lineCount = this.cminstance.lineCount()

      const combinedHint = this.combinedHint
      this.cminstance.on('keyup', function (cm, event) {
        if (cm.state.completionActive || ignoredKeyCodesForAutocomplete.includes(event.code)) {
          return
        }
        if (cm.options.showHint) {
          cm.showHint({ hint: combinedHint, completeSingle: false })
        }
      })

      EventBus.$on(`change-editor-${this.name}`, this.changeEditorValue)

      this.cminstance.on('change', (cm) => {
        this.content = cm.getValue()
        cm.save()
        if (this.$emit) {
          this.$emit('input', this.content)
        }
      })

      const tmpEvents = {}
      allEvents
        .concat(this.events)
        .concat(this.globalEvents)
        .filter((e) => !tmpEvents[e] && (tmpEvents[e] = true))
        .forEach((event) => {
          this.cminstance.on(event, (...args) => {
            this.$emit(event, ...args)
            const lowerCaseEvent = event.replace(/([A-Z])/g, '-$1').toLowerCase()
            if (lowerCaseEvent !== event) {
              this.$emit(lowerCaseEvent, ...args)
            }
          })
        })

      this.initTern()
      this.$emit('ready', this.codemirror)
      this.unseenLineMarkers()
    },
    changeEditorValue(value) {
      this.cminstance.setValue(value)
    },
    initTern() {
      // LOCAL -> rewrite and load fields, queries, etc...
      const getData = async () => {
        const dynamicDefs = []

        // @todo mock endpoint
        // const { data } = await this.$client.execute({
        //   operationId: 'List_getCode',
        //   parameters: {}
        // })
        const data = '{}'

        Object.assign(this.ternConfig, ecmascript, JSON.parse(data))
        Object.entries(this.ternConfig).forEach((config, index) => {
          dynamicDefs.push({ [config[0]]: config[1] })
        })

        const server = new window.CodeMirror.TernServer({
          defs: [...dynamicDefs],
        })

        this.cminstance.setOption('extraKeys', {
          'Ctrl-Space': function (cm) {
            server.complete(cm)
          },
          'Ctrl-I': function (cm) {
            server.showType(cm)
          },
          'Ctrl-O': function (cm) {
            server.showDocs(cm)
          },
          'Alt-.': function (cm) {
            server.jumpToDef(cm)
          },
          'Alt-,': function (cm) {
            server.jumpBack(cm)
          },
          'Ctrl-Q': function (cm) {
            server.rename(cm)
          },
          'Ctrl-.': function (cm) {
            server.selectName(cm)
          },
        })

        this.cminstance.on('cursorActivity', function (cm) {
          server.updateArgHints(cm)
        })
      }

      getData()
    },
    destroy() {
      const element = this.cminstance.doc.cm.getWrapperElement()
      element && element.remove && element.remove()
    },
    codeChangeHandler(newVal) {
      newVal = get(newVal, 'exp', newVal)
      const cm_value = this.cminstance.getValue()
      this.content = newVal
      if (newVal !== cm_value && !isNil(newVal)) {
        const scrollInfo = this.cminstance.getScrollInfo()
        this.cminstance.setValue(newVal)
        this.cminstance.scrollTo(scrollInfo.left, scrollInfo.top)
      }
      this.unseenLineMarkers()
      this.lineCount = this.cminstance.lineCount()
    },
    unseenLineMarkers() {
      if (this.unseenLines !== undefined && this.marker !== undefined) {
        this.unseenLines.forEach((line) => {
          const info = this.cminstance.lineInfo(line)
          this.cminstance.setGutterMarker(line, 'breakpoints', info.gutterMarkers ? null : this.marker())
        })
      }
    },
    combinedHint(cm, options) {
      const cur = cm.getCursor()
      const token = cm.getTokenAt(cur)
      const start = token.start
      const end = token.end
      const word = token.string
      const mode = cm.getOption('mode')
      const safeWords = ['{', '(', ';', ',']

      if (!word || safeWords.includes(word)) {
        return { list: [], from: CodeMirror.Pos(cur.line, start), to: CodeMirror.Pos(cur.line, end) }
      }

      const customList = this.replacementMap
        .filter((replacement) => {
          return replacement.trigger?.startsWith(word)
        })
        .map((replacement) => {
          const replacementObj = this.replacementMap.find((r) => r.trigger === replacement.trigger)
          return {
            text: replacementObj.replaceText,
            displayText: replacementObj.displayText,
            from: CodeMirror.Pos(cur.line, start),
            to: CodeMirror.Pos(cur.line, end),
          }
        })

      let standardHintFunction
      switch (mode) {
        case EDITOR_MODES.VUE:
        case EDITOR_MODES.JAVASCRIPT:
          standardHintFunction = CodeMirror.hint.javascript
          break
        case EDITOR_MODES.HTML_MIXED:
        case EDITOR_MODES.HTML:
          standardHintFunction = CodeMirror.hint.html
          break
        case EDITOR_MODES.CSS:
          standardHintFunction = CodeMirror.hint.css
          break
        case EDITOR_MODES.SQL:
          standardHintFunction = CodeMirror.hint.sql
          break
        default:
          standardHintFunction = CodeMirror.hint.anyword
      }
      const standardHints = standardHintFunction(cm, options)
      const combinedList = [...customList, ...standardHints.list]

      return {
        list: combinedList,
        from: CodeMirror.Pos(cur.line, start),
        to: CodeMirror.Pos(cur.line, end),
      }
    },
  },
  mounted() {
    window.CodeMirror = CodeMirror
    window.tern = tern
    this.content = this.code || get(this.value, 'exp', this.value) || ''
    this.initialize()
  },
  beforeDestroy() {
    this.destroy()
  },
}
</script>
<style scoped>
.fullscreen {
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000000000;
}
</style>
<style lang="scss">
.CodeMirror-hints {
  z-index: 10000000;
}

.CodeMirror {
  font-size: 16px;
  line-height: 20px;
  border-radius: 6px;
}
.expression-editor {
  position: relative;
  height: 100%;
  transition: 0.3s all;
}
.expression-editor-textarea {
  display: flex !important;
  transform: translateX(-10000px);
  height: 0;
  position: absolute;
}
.expression-editor .change-style {
  position: absolute;
  right: 0.4em;
  top: 0.8em;
  color: whitesmoke;
  cursor: pointer;
  -webkit-transition: 0.3s all;
  transition: 0.3s all;
  background: #262626;
  padding: 0.5em 1em;
  border-radius: 0.5em;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  width: fit-content;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  gap: 0.5em;
}

.expression-editor .material-icons {
  transition: 0.3s all;
}

.expression-editor .material-icons:hover {
  transform: scale(1.25);
}

.change-style .el-select {
  width: 0px;
  height: 0px;
  overflow: hidden;
  transform: translate(-40px, -35px);
}

.code-syntax-check {
  position: absolute;
  bottom: 0;
  background: $verification-error-color-light;
  width: 100%;
  padding: 1em;
  font-weight: 500;
  color: whitesmoke;
}

.disableMinHeight .CodeMirror-scroll {
  min-height: initial;
}

.CodeMirror-scroll {
  min-height: $code-editor-height;
}

.editor-wrapper .CodeMirror {
  position: relative;
}
</style>
